import { apiFetch } from "../api/client";
import React, {
  useCallback,
  useEffect,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { tenantPath } from "../utils/tenantContext";
import { useTenantScope } from "../scope/TenantScopeContext";
import API from "../config";

function formatDate(value) {
  if (!value) return "N/A";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleString();
}

function formatLabel(value) {
  if (!value) return "Unknown";

  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) =>
      letter.toUpperCase()
    );
}

function scoreClass(score) {
  const numericScore = Number(score || 0);

  if (numericScore >= 90) {
    return "compliance-score compliance-score-excellent";
  }

  if (numericScore >= 75) {
    return "compliance-score compliance-score-good";
  }

  if (numericScore >= 50) {
    return "compliance-score compliance-score-partial";
  }

  return "compliance-score compliance-score-low";
}

function ComplianceCenterPage() {
  const scope = useTenantScope();
  const [showCreateAssessment, setShowCreateAssessment] =
    useState(false);
  const [creatingAssessment, setCreatingAssessment] =
    useState(false);
  const [createAssessmentError, setCreateAssessmentError] =
    useState("");

  const [assessmentForm, setAssessmentForm] = useState({
    framework_id: "",
    assessment_name: "",
    assessment_type: "continuous",
    scope_description: "",
    assessment_owner: "",
  });

  const [complianceRules, setComplianceRules] = useState([]);
  const [selectedComplianceRule, setSelectedComplianceRule] =
    useState(null);
  const [ruleLoadError, setRuleLoadError] = useState("");

  const [runnerForm, setRunnerForm] = useState({
    framework_id: "",
    device_uuid: "",
  });

  const [runningAssessment, setRunningAssessment] =
    useState(false);

  const [runnerResult, setRunnerResult] =
    useState(null);

  const [runnerError, setRunnerError] =
    useState("");

  const [summary, setSummary] = useState(null);
  const [frameworks, setFrameworks] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [remediationTasks, setRemediationTasks] = useState([]);
  const [remediationSummary, setRemediationSummary] = useState(null);
  const [cmmcReadiness, setCmmcReadiness] = useState(null);
  const [updatingTaskId, setUpdatingTaskId] = useState(null);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [collectingId, setCollectingId] = useState(null);
  const [error, setError] = useState("");

  const loadCompliance = useCallback(
    async (silent = false) => {
      try {
        if (silent) {
          setRefreshing(true);
        } else {
          setLoading(true);
        }

        setError("");

        const [
          summaryResponse,
          frameworksResponse,
          assessmentsResponse,
          remediationResponse,
          remediationSummaryResponse,
          cmmcReadinessResponse,
        ] = await Promise.all([
          apiFetch(tenantPath(`/compliance/summary`)),
          apiFetch(`/compliance/frameworks`),
          apiFetch(tenantPath(`/compliance/assessments?limit=100`)),
          apiFetch(tenantPath(`/compliance/remediation?limit=100`)),
          apiFetch(tenantPath(`/compliance/remediation/summary`)),
          apiFetch(tenantPath(`/compliance/cmmc/readiness`)),
        ]);

        const summaryData = await summaryResponse.json();
        const frameworksData =
          await frameworksResponse.json();
        const assessmentsData =
          await assessmentsResponse.json();
        const remediationData =
          await remediationResponse.json();
        const remediationSummaryData =
          await remediationSummaryResponse.json();

        const cmmcReadinessData =
          await cmmcReadinessResponse.json();

        if (!summaryResponse.ok) {
          throw new Error(
            summaryData.detail ||
              "Unable to load compliance summary."
          );
        }

        if (!frameworksResponse.ok) {
          throw new Error(
            frameworksData.detail ||
              "Unable to load compliance frameworks."
          );
        }

        if (!assessmentsResponse.ok) {
          throw new Error(
            assessmentsData.detail ||
              "Unable to load compliance assessments."
          );
        }

        if (!remediationResponse.ok) {
          throw new Error(
            remediationData.detail ||
              "Unable to load compliance remediation tasks."
          );
        }

        if (!remediationSummaryResponse.ok) {
          throw new Error(
            remediationSummaryData.detail ||
              "Unable to load remediation summary."
          );
        }

        if (!cmmcReadinessResponse.ok) {
          throw new Error(
            cmmcReadinessData.detail ||
              "Unable to load CMMC readiness."
          );
        }

        setSummary(summaryData);

        setFrameworks(
          Array.isArray(frameworksData)
            ? frameworksData
            : []
        );

        setAssessments(
          Array.isArray(assessmentsData)
            ? assessmentsData
            : []
        );

        setRemediationTasks(
          Array.isArray(remediationData)
            ? remediationData
            : []
        );

        setRemediationSummary(
          remediationSummaryData || null
        );

        setCmmcReadiness(
          cmmcReadinessData || null
        );
      } catch (loadError) {
        console.error(
          "Compliance Center load error:",
          loadError
        );

        setError(loadError.message);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    loadCompliance();
  }, [loadCompliance]);

  useEffect(() => {
    const handleTenantChange = () => {
      loadCompliance();
    };

    window.addEventListener(
      "wirelesspress-tenant-change",
      handleTenantChange
    );

    return () => {
      window.removeEventListener(
        "wirelesspress-tenant-change",
        handleTenantChange
      );
    };
  }, [loadCompliance]);

  const runAutomatedAssessment = async () => {
    if (!runnerForm.framework_id) {
      setRunnerError(
        "Select a compliance framework."
      );
      return;
    }

    try {
      setRunningAssessment(true);
      setRunnerError("");
      setRunnerResult(null);

      const selectedFramework =
        frameworks.find(
          (framework) =>
            String(framework.id) ===
            String(runnerForm.framework_id)
        );

      if (!selectedFramework) {
        throw new Error(
          "Selected compliance framework was not found."
        );
      }

      const nistFramework =
        frameworks.find(
          (framework) =>
            String(
              framework.framework_key || ""
            ).toLowerCase() === "nist-csf"
        );

      if (!nistFramework) {
        throw new Error(
          "NIST CSF evidence framework is unavailable."
        );
      }

      const selectedDevice =
        runnerForm.device_uuid
          ? scope.devices.find(
              (device) =>
                String(device.device_uuid) ===
                String(runnerForm.device_uuid)
            )
          : null;

      const isNistSelection =
        String(
          selectedFramework.framework_key || ""
        ).toLowerCase() === "nist-csf";

      //
      // Wireless Press uses NIST CSF as the canonical
      // automated evidence layer.
      //
      // Other frameworks consume only validated mappings
      // from the same company/device-scoped NIST result.
      //
      const evidenceFrameworkId =
        Number(nistFramework.id);

      const prepareResponse = await apiFetch(
        tenantPath(
          `/compliance/assessments/run`
        ),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            company_id: Number(
              scope.companyId
            ),
            framework_id:
              evidenceFrameworkId,
            device_uuid:
              runnerForm.device_uuid || null,
          }),
        }
      );

      const prepared =
        await prepareResponse.json();

      if (!prepareResponse.ok) {
        throw new Error(
          prepared.detail ||
            "Unable to prepare evidence assessment."
        );
      }

      const sourceAssessmentId =
        prepared.assessment?.id;

      if (!sourceAssessmentId) {
        throw new Error(
          "Evidence assessment ID was not returned."
        );
      }

      const evidenceResponse = await apiFetch(
        tenantPath(
          `/compliance/assessments/${sourceAssessmentId}/collect-evidence`
        ),
        {
          method: "POST",
        }
      );

      const evidence =
        await evidenceResponse.json();

      if (!evidenceResponse.ok) {
        throw new Error(
          evidence.detail ||
            "Evidence collection failed."
        );
      }

      const ruleResponse = await apiFetch(
        tenantPath(
          `/compliance/assessments/${sourceAssessmentId}/evaluate-rules`
        ),
        {
          method: "POST",
        }
      );

      const rules =
        await ruleResponse.json();

      if (!ruleResponse.ok) {
        throw new Error(
          rules.detail ||
            "Rule evaluation failed."
        );
      }

      const scoreResponse = await apiFetch(
        tenantPath(
          `/compliance/assessments/${sourceAssessmentId}/score`
        ),
        {
          method: "POST",
        }
      );

      const sourceScore =
        await scoreResponse.json();

      if (!scoreResponse.ok) {
        throw new Error(
          sourceScore.detail ||
            "Evidence assessment score calculation failed."
        );
      }

      let finalAssessment =
        sourceScore.assessment ||
        prepared.assessment;

      let propagation = null;

      let finalScore = sourceScore;

      if (!isNistSelection) {
        const selectedFrameworkId =
          Number(selectedFramework.id);

        const propagateResponse =
          await apiFetch(
            tenantPath(
              `/compliance/assessments/${sourceAssessmentId}/propagate-mappings` +
              `?create_missing_assessments=true` +
              `&target_framework_id=${selectedFrameworkId}`
            ),
            {
              method: "POST",
            }
          );

        propagation =
          await propagateResponse.json();

        if (!propagateResponse.ok) {
          throw new Error(
            propagation.detail ||
              "Cross-framework assessment mapping failed."
          );
        }

        const targetAssessment =
          (
            propagation.recalculated_assessments ||
            []
          ).find(
            (assessment) =>
              Number(assessment.framework_id) ===
              selectedFrameworkId
          );

        if (!targetAssessment) {
          throw new Error(
            "No validated automated mappings are available " +
            "for the selected framework and scope."
          );
        }

        finalAssessment = {
          id:
            targetAssessment.assessment_id,

          assessment_name:
            targetAssessment.assessment_name ||
            (
              `${selectedFramework.framework_name} - ` +
              (
                selectedDevice?.hostname ||
                "All Managed Devices"
              )
            ),

          framework_id:
            selectedFrameworkId,

          device_uuid:
            targetAssessment.device_uuid ??
            runnerForm.device_uuid ??
            null,

          overall_score:
            targetAssessment.overall_score,

          maturity_level:
            targetAssessment.maturity_level,

          score_metrics:
            targetAssessment.score_metrics ||
            {},
        };

        finalScore = {
          status: "completed",
          assessment: finalAssessment,
          source_assessment:
            sourceScore.assessment ||
            prepared.assessment,
          propagation,
        };
      }

      setRunnerResult({
        assessment:
          finalAssessment,

        sourceAssessment:
          prepared.assessment,

        target:
          prepared.target,

        created:
          prepared.created,

        framework:
          selectedFramework,

        evidenceFramework:
          nistFramework,

        evidence,

        rules,

        score:
          finalScore,

        propagation,

        mapped:
          !isNistSelection,
      });

      await loadCompliance(true);

    } catch (error) {
      console.error(
        "Automated compliance assessment:",
        error
      );

      setRunnerError(
        error?.message ||
          "Automated assessment failed."
      );

    } finally {
      setRunningAssessment(false);
    }
  };


  const createAssessment = async () => {
    if (!assessmentForm.framework_id) {
      setCreateAssessmentError(
        "Select a compliance framework."
      );
      return;
    }

    if (!assessmentForm.assessment_name.trim()) {
      setCreateAssessmentError(
        "Assessment name is required."
      );
      return;
    }

    try {
      setCreatingAssessment(true);
      setCreateAssessmentError("");

      const username =
        localStorage.getItem("username") || "";

      const payload = {
        framework_id: Number(
          assessmentForm.framework_id
        ),
        assessment_name:
          assessmentForm.assessment_name.trim(),
        assessment_type:
          assessmentForm.assessment_type,
        scope_description:
          assessmentForm.scope_description.trim() ||
          null,
        assessment_owner:
          assessmentForm.assessment_owner.trim() ||
          username ||
          null,
      };

      if (username) {
        payload.created_by = username;
      }

      const response = await apiFetch(
        tenantPath(`/compliance/assessments`),
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to create compliance assessment."
        );
      }

      setAssessmentForm({
        framework_id: "",
        assessment_name: "",
        assessment_type: "continuous",
        scope_description: "",
        assessment_owner: "",
      });

      setShowCreateAssessment(false);

      await loadCompliance(true);
    } catch (createError) {
      console.error(
        "Create assessment error:",
        createError
      );

      setCreateAssessmentError(
        createError.message
      );
    } finally {
      setCreatingAssessment(false);
    }
  };

  const loadComplianceRules = useCallback(async () => {
    try {
      setRuleLoadError("");

      const response = await apiFetch(
        `/compliance/rules?enabled=true&limit=500`
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail || "Unable to load compliance rules."
        );
      }

      setComplianceRules(
        Array.isArray(data) ? data : []
      );
    } catch (ruleError) {
      console.error(
        "Compliance rule load error:",
        ruleError
      );

      setRuleLoadError(ruleError.message);
    }
  }, []);

  useEffect(() => {
    loadComplianceRules();
  }, [loadComplianceRules]);

  const collectEvidence = async (assessmentId) => {
    try {
      setCollectingId(assessmentId);

      const response = await apiFetch(`/compliance/assessments/${assessmentId}/collect-evidence`,
        {
          method: "POST",
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to collect automated evidence."
        );
      }

      await loadCompliance(true);

      window.alert(
        `${data.controls_evaluated || 0} control(s) evaluated.`
      );
    } catch (collectError) {
      console.error(
        "Compliance evidence collection error:",
        collectError
      );

      window.alert(collectError.message);
    } finally {
      setCollectingId(null);
    }
  };

  const updateRemediationTask = async (
    taskId,
    status
  ) => {
    try {
      setUpdatingTaskId(taskId);

      const response = await apiFetch(`/compliance/remediation/${taskId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
          company_id: Number(
            scope.companyId
          ),
            status,
            assigned_to:
              localStorage.getItem("username") ||
              "Authenticated Administrator",
            assigned_team:
              "Wireless Press Engineering",
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.detail ||
            "Unable to update remediation task."
        );
      }

      await loadCompliance(true);
    } catch (updateError) {
      console.error(
        "Remediation task update error:",
        updateError
      );

      window.alert(updateError.message);
    } finally {
      setUpdatingTaskId(null);
    }
  };

  if (loading) {
    return (
      <div className="compliance-loading">
        Loading Wireless Press Compliance Center...
      </div>
    );
  }

  const frameworkMetrics =
    summary?.framework_metrics || {};

  const assessmentMetrics =
    summary?.assessment_metrics || {};

  const remediationMetrics =
    summary?.remediation_metrics || {};

  const cmmcItems = Array.isArray(cmmcReadiness?.items)
    ? cmmcReadiness.items
    : [];

  const cmmcItem = cmmcItems.length > 0
    ? cmmcItems[0]
    : null;

  const cmmcAssessment =
    cmmcItem?.assessment || {};

  const cmmcMetrics =
    cmmcItem?.readiness || {};

  const cmmcLevels = Array.isArray(cmmcItem?.levels)
    ? cmmcItem.levels
    : [];

  const cmmcFamilies = Array.isArray(cmmcItem?.families)
    ? cmmcItem.families
    : [];

  const cmmcBlockingControls = Array.isArray(
    cmmcItem?.blocking_controls
  )
    ? cmmcItem.blocking_controls
    : [];

  return (
    <div className="compliance-page">
      <section
        className="compliance-panel compliance-assessment-runner"
        data-wp-compliance-runner="device-framework"
      >
        <div className="compliance-panel-heading">
          <div>
            <span>AUTOMATED ASSESSMENT</span>
            <h3>Run Compliance Assessment</h3>
          </div>
        </div>

        <p>
          Select one active managed endpoint or run the
          framework across all active managed devices.
          Wireless Press will collect available evidence,
          evaluate supported rules, calculate the score,
          and refresh the assessment automatically.
        </p>

        <div className="compliance-remediation-summary">
          <label>
            Device
            <select
              value={runnerForm.device_uuid}
              onChange={(event) =>
                setRunnerForm((current) => ({
                  ...current,
                  device_uuid:
                    event.target.value,
                }))
              }
            >
              <option value="">
                All Active Managed Devices
              </option>

              {(scope.devices || [])
                .filter(
                  (device) =>
                    String(
                      device.lifecycle_state || ""
                    ).toLowerCase() === "active"
                )
                .map((device) => (
                  <option
                    key={device.device_uuid}
                    value={device.device_uuid}
                  >
                    {device.hostname ||
                      device.device_name ||
                      `Device ${device.id}`}
                    {" · "}
                    {device.platform ||
                      "Unknown platform"}
                    {" · "}
                    {device.status ||
                      "Unknown status"}
                  </option>
                ))}
            </select>
          </label>

          <label>
            Framework
            <select
              value={runnerForm.framework_id}
              onChange={(event) =>
                setRunnerForm((current) => ({
                  ...current,
                  framework_id:
                    event.target.value,
                }))
              }
            >
              <option value="">
                Select framework
              </option>

              {frameworks
                .filter(
                  (framework) =>
                    framework.enabled
                )
                .map((framework) => (
                  <option
                    key={framework.id}
                    value={framework.id}
                  >
                    {framework.framework_name}
                    {" — "}
                    {framework.version ||
                      "Current"}
                  </option>
                ))}
            </select>
          </label>
        </div>

        <button
          type="button"
          onClick={runAutomatedAssessment}
          disabled={
            runningAssessment ||
            !scope.companyId ||
            !runnerForm.framework_id
          }
        >
          {runningAssessment
            ? "Running Assessment..."
            : "Run Assessment"}
        </button>

        {runnerError && (
          <div
            className="compliance-warning"
            role="alert"
          >
            {runnerError}
          </div>
        )}

        {runnerResult && (
          <div className="compliance-remediation-summary">
            <div>
              <span>Assessment</span>
              <strong>
                {runnerResult.assessment
                  ?.assessment_name}
              </strong>
            </div>

            <div>
              <span>Device Scope</span>
              <strong>
                {runnerResult.target
                  ?.hostname ||
                  "All Active Managed Devices"}
              </strong>
            </div>

            <div>
              <span>Assessment Record</span>
              <strong>
                {runnerResult.created
                  ? "Created"
                  : "Updated"}
              </strong>
            </div>

            <div>
              <span>Framework</span>
              <strong>
                {runnerResult.framework
                  ?.framework_name ||
                  "Compliance Framework"}
              </strong>
            </div>

            <div>
              <span>Evidence-backed Score</span>
              <strong>
                {Number(
                  runnerResult.assessment
                    ?.overall_score || 0
                ).toFixed(1)}
                %
              </strong>
            </div>

            <div>
              <span>Assessment Coverage</span>
              <strong>
                {(() => {
                  //
                  // Coverage must come from the latest score
                  // calculation whenever possible.
                  //
                  // Do not allow stale assessment-level
                  // score_metrics to override the response
                  // returned by the just-completed /score call.
                  //
                  const metrics =
                    runnerResult.score?.score_metrics ||
                    runnerResult.score?.metrics ||
                    runnerResult.assessment?.score_metrics ||
                    {};

                  const assessed =
                    Number(metrics.assessed_controls);

                  const assessable =
                    Number(metrics.assessable_controls);

                  const hasAuthoritativeCounts =
                    Number.isFinite(assessed) &&
                    Number.isFinite(assessable) &&
                    assessable > 0;

                  const coverage =
                    hasAuthoritativeCounts
                      ? (assessed / assessable) * 100
                      : Number(
                          metrics.coverage_percent ?? 0
                        );

                  return (
                    Number.isFinite(coverage)
                      ? coverage
                      : 0
                  ).toFixed(1);
                })()}
                %
              </strong>
            </div>
          </div>
        )}

        <small>
          Controls without authoritative evidence remain
          Not Assessed rather than being treated as an
          automatic security failure.
        </small>
      </section>

      <section className="compliance-header">
        <div>
          <div className="compliance-eyebrow">
            GOVERNANCE, RISK & COMPLIANCE
          </div>

          <h2>Compliance Center</h2>

          <p>
            Measure security-control implementation, collect
            automated evidence, track remediation, and prepare
            for formal audits.
          </p>
        </div>

        <button
          type="button"
          onClick={() => loadCompliance(true)}
          disabled={refreshing}
        >
          {refreshing
            ? "Refreshing..."
            : "Refresh Compliance"}
        </button>
      </section>

      {error && (
        <div className="compliance-warning">
          Compliance refresh failed: {error}
        </div>
      )}

      <section className="compliance-metric-grid">
        <article>
          <span>Enabled Frameworks</span>
          <strong>
            {frameworkMetrics.enabled_frameworks || 0}
          </strong>
          <small>
            {frameworkMetrics.total_frameworks || 0} total
          </small>
        </article>

        <article>
          <span>Active Assessments</span>
          <strong>
            {assessmentMetrics.active_assessments || 0}
          </strong>
          <small>
            {assessmentMetrics.total_assessments || 0} total
          </small>
        </article>

        <article>
          <span>Average Score</span>
          <strong>
            {Number(
              assessmentMetrics.average_score || 0
            ).toFixed(1)}
            %
          </strong>
          <small>Across all assessments</small>
        </article>

        <article className="compliance-remediation-card">
          <span>Open Remediation</span>
          <strong>
            {remediationMetrics.open_tasks || 0}
          </strong>
          <small>
            {remediationMetrics.critical_open_tasks || 0}
            {" "}critical
          </small>
        </article>
      </section>

      <section className="compliance-grid">
        <article className="compliance-panel">
          <div className="compliance-panel-heading">
            <div>
              <span>FRAMEWORK LIBRARY</span>
              <h3>Compliance Frameworks</h3>
            </div>
          </div>

          <div className="compliance-framework-list">
            {frameworks.map((framework) => (
              <div
                className="compliance-framework-item"
                key={framework.id}
              >
                <div>
                  <strong>
                    {framework.framework_name}
                  </strong>

                  <span>
                    Version {framework.version || "Current"}
                  </span>

                  <small>
                    {framework.control_count || 0}
                    {" "}loaded controls
                  </small>
                </div>

                <span
                  className={
                    framework.enabled
                      ? "compliance-framework-enabled"
                      : "compliance-framework-disabled"
                  }
                >
                  {framework.enabled
                    ? "Enabled"
                    : "Disabled"}
                </span>
              </div>
            ))}
          </div>
        </article>

        <article className="compliance-panel">
          <div className="compliance-panel-heading">
            <div>
              <span>MATURITY OVERVIEW</span>
              <h3>Assessment Health</h3>
            </div>
          </div>

          <div className="compliance-health-list">
            {assessments.length > 0 ? (
              assessments.slice(0, 8).map((assessment) => (
                <div
                  className="compliance-health-item"
                  key={assessment.id}
                >
                  <div>
                    <strong>
                      {assessment.assessment_name}
                    </strong>

                    <span>
                      {assessment.framework_name}
                    </span>
                  </div>

                  <div
                    className={scoreClass(
                      assessment.overall_score
                    )}
                  >
                    {Number(
                      assessment.overall_score || 0
                    ).toFixed(1)}
                    %
                  </div>
                </div>
              ))
            ) : (
              <div className="compliance-empty">
                No assessments created.
              </div>
            )}
          </div>
        </article>
      </section>

      <section className="compliance-panel compliance-cmmc-panel">
        <div className="compliance-panel-heading compliance-cmmc-heading">
          <div>
            <span>CMMC 2.0 READINESS</span>
            <h3>Certification Readiness Overview</h3>
            <p>
              Current readiness applies only to controls loaded
              into the Wireless Press CMMC catalog.
            </p>
          </div>

          {cmmcAssessment.id && (
            <Link
              to={`/compliance/assessments/${cmmcAssessment.id}`}
              className="compliance-cmmc-review-link"
            >
              Review Assessment
            </Link>
          )}
        </div>

        {cmmcItem ? (
          <>
            <div className="compliance-cmmc-metrics">
              <article>
                <span>Overall Readiness</span>
                <strong>
                  {Number(
                    cmmcMetrics.overall_score || 0
                  ).toFixed(1)}
                  %
                </strong>
                <small>
                  {formatLabel(
                    cmmcMetrics.readiness_status
                  )}
                </small>
              </article>

              <article>
                <span>Implemented</span>
                <strong>
                  {cmmcMetrics.implemented_controls || 0}
                </strong>
                <small>
                  of {cmmcMetrics.total_controls || 0}
                  {" "}loaded controls
                </small>
              </article>

              <article>
                <span>Blocking Controls</span>
                <strong>
                  {cmmcMetrics.blocking_control_count || 0}
                </strong>
                <small>
                  Must be resolved before readiness
                </small>
              </article>

              <article>
                <span>Evidence Coverage</span>
                <strong>
                  {Number(
                    cmmcMetrics.evidence_coverage_percent || 0
                  ).toFixed(1)}
                  %
                </strong>
                <small>
                  {cmmcMetrics.controls_with_evidence || 0}
                  {" "}controls with evidence
                </small>
              </article>

              <article>
                <span>Open Remediation</span>
                <strong>
                  {cmmcMetrics.open_remediation_tasks || 0}
                </strong>
                <small>
                  Active CMMC remediation tasks
                </small>
              </article>
            </div>

            <div className="compliance-cmmc-layout">
              <div className="compliance-cmmc-family-panel">
                <div className="compliance-cmmc-section-title">
                  <span>DOMAIN PERFORMANCE</span>
                  <strong>CMMC Control Families</strong>
                </div>

                <div className="compliance-cmmc-family-list">
                  {cmmcFamilies.map((family) => {
                    const score = Math.max(
                      0,
                      Math.min(
                        100,
                        Number(family.average_score || 0)
                      )
                    );

                    return (
                      <div
                        className="compliance-cmmc-family"
                        key={family.control_family}
                      >
                        <div className="compliance-cmmc-family-meta">
                          <div>
                            <strong>
                              {family.control_family}
                            </strong>

                            <span>
                              {family.implemented || 0}
                              {" / "}
                              {family.control_count || 0}
                              {" "}implemented
                            </span>
                          </div>

                          <b>{score.toFixed(1)}%</b>
                        </div>

                        <div className="compliance-cmmc-progress">
                          <div
                            style={{
                              width: `${score}%`,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="compliance-cmmc-blocker-panel">
                <div className="compliance-cmmc-section-title">
                  <span>CERTIFICATION BLOCKERS</span>
                  <strong>
                    Controls Requiring Attention
                  </strong>
                </div>

                <div className="compliance-cmmc-blocker-list">
                  {cmmcBlockingControls.length > 0 ? (
                    cmmcBlockingControls.map((control) => (
                      <div
                        className="compliance-cmmc-blocker"
                        key={control.id}
                      >
                        <div>
                          <strong>
                            {control.control_identifier}
                          </strong>

                          <span>
                            {control.control_title}
                          </span>

                          <small>
                            {control.control_family}
                            {" · "}
                            {formatLabel(control.status)}
                          </small>
                        </div>

                        <div>
                          <span
                            className={`compliance-task-priority compliance-task-priority-${String(
                              control.priority || "P3"
                            ).toLowerCase()}`}
                          >
                            {control.priority || "P3"}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="compliance-empty">
                      No blocking CMMC controls detected.
                    </div>
                  )}
                </div>
              </div>
            </div>

            {cmmcLevels.length > 0 && (
              <div className="compliance-cmmc-level-summary">
                {cmmcLevels.map((level) => (
                  <div key={level.level}>
                    <span>
                      CMMC Level {level.level}
                    </span>

                    <strong>
                      {Number(
                        level.average_score || 0
                      ).toFixed(1)}
                      %
                    </strong>

                    <small>
                      {level.implemented || 0}
                      {" "}implemented ·{" "}
                      {level.failed || 0}
                      {" "}failed ·{" "}
                      {level.not_assessed || 0}
                      {" "}not assessed
                    </small>
                  </div>
                ))}
              </div>
            )}

            {!cmmcMetrics.catalog_complete && (
              <div className="compliance-cmmc-notice">
                <strong>Catalog coverage notice</strong>
                <span>
                  {cmmcMetrics.catalog_notice ||
                    "The loaded CMMC catalog is incomplete."}
                </span>
              </div>
            )}
          </>
        ) : (
          <div className="compliance-empty">
            No CMMC readiness assessment is currently available.
          </div>
        )}
      </section>

      <section className="compliance-panel">
        <div className="compliance-panel-heading compliance-remediation-heading">
          <div>
            <span>REMEDIATION WORKFLOW</span>
            <h3>Compliance Remediation Queue</h3>
          </div>

          <div className="compliance-remediation-summary">
            <span>
              Open:
              <strong>
                {remediationSummary?.metrics?.open_tasks || 0}
              </strong>
            </span>

            <span>
              P1:
              <strong>
                {remediationSummary?.metrics?.p1_open_tasks || 0}
              </strong>
            </span>

            <span>
              Overdue:
              <strong>
                {remediationSummary?.metrics?.overdue_tasks || 0}
              </strong>
            </span>
          </div>
        </div>

        <div className="compliance-table-wrapper">
          <table className="compliance-table">
            <thead>
              <tr>
                <th>Priority</th>
                <th>Control</th>
                <th>Finding</th>
                <th>Assigned</th>
                <th>Status</th>
                <th>Due</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {remediationTasks.length > 0 ? (
                remediationTasks.map((task) => (
                  <tr key={task.id}>
                    <td>
                      <span
                        className={`compliance-task-priority compliance-task-priority-${String(
                          task.priority || "P3"
                        ).toLowerCase()}`}
                      >
                        {task.priority || "P3"}
                      </span>
                    </td>

                    <td>
                      <strong>
                        {task.control_identifier}
                      </strong>

                      <span className="compliance-cell-subtext">
                        {task.control_title}
                      </span>
                    </td>

                    <td>
                      <span className="compliance-task-description">
                        {task.description ||
                          "No finding description"}
                      </span>
                    </td>

                    <td>
                      {task.assigned_to || "Unassigned"}

                      <span className="compliance-cell-subtext">
                        {task.assigned_team || ""}
                      </span>
                    </td>

                    <td>
                      {formatLabel(task.status)}
                    </td>

                    <td>
                      {formatDate(task.due_at)}
                    </td>

                    <td>
                      <select
                        value={task.status}
                        disabled={
                          updatingTaskId === task.id
                        }
                        onChange={(event) =>
                          updateRemediationTask(
                            task.id,
                            event.target.value
                          )
                        }
                      >
                        <option value="open">Open</option>
                        <option value="in_progress">
                          In Progress
                        </option>
                        <option value="blocked">
                          Blocked
                        </option>
                        <option value="completed">
                          Completed
                        </option>
                        <option value="cancelled">
                          Cancelled
                        </option>
                      </select>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="7"
                    className="compliance-empty"
                  >
                    No compliance remediation tasks found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="compliance-panel">
        <div className="compliance-panel-heading">
          <div>
            <span>CONTINUOUS ASSESSMENT</span>
            <h3>Compliance Assessments</h3>
            <button
              type="button"
              onClick={() =>
                setShowCreateAssessment(true)
              }
            >
              Create Assessment
            </button>

          </div>
        </div>

        <div className="compliance-table-wrapper">
          <table className="compliance-table">
            <thead>
              <tr>
                <th>Assessment</th>
                <th>Framework</th>
                <th>Score</th>
                <th>Maturity</th>
                <th>Controls</th>
                <th>Remediation</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {assessments.length > 0 ? (
                assessments.map((assessment) => (
                  <tr key={assessment.id}>
                    <td>
                      <Link
                        to={`/compliance/assessments/${assessment.id}`}
                        className="compliance-assessment-link"
                      >
                        <strong>
                          {assessment.assessment_name}
                        </strong>

                        <span>
                          {assessment.company_name ||
                            "Platform-wide"}
                        </span>
                      </Link>
                    </td>

                    <td>
                      {assessment.framework_name}
                      <span className="compliance-cell-subtext">
                        {assessment.version}
                      </span>
                    </td>

                    <td>
                      <span
                        className={scoreClass(
                          assessment.overall_score
                        )}
                      >
                        {Number(
                          assessment.overall_score || 0
                        ).toFixed(1)}
                        %
                      </span>
                    </td>

                    <td>
                      {formatLabel(
                        assessment.maturity_level
                      )}
                    </td>

                    <td>
                      {assessment.implemented_count || 0}
                      {" / "}
                      {assessment.control_count || 0}
                    </td>

                    <td>
                      {assessment.remediation_count || 0}
                    </td>

                    <td>
                      {formatDate(assessment.updated_at)}
                    </td>

                    <td>
                      <div className="compliance-row-actions">
                        <Link
                          to={`/compliance/assessments/${assessment.id}`}
                        >
                          Review
                        </Link>

                        <button
                          type="button"
                          disabled={
                            collectingId === assessment.id
                          }
                          onClick={() =>
                            collectEvidence(assessment.id)
                          }
                        >
                          {collectingId === assessment.id
                            ? "Collecting..."
                            : "Collect Evidence"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="8"
                    className="compliance-empty"
                  >
                    No compliance assessments found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
      <section className="compliance-panel compliance-rule-catalog">
        <div className="compliance-panel-heading">
          <div>
            <span>AUTOMATED COMPLIANCE ENGINE</span>
            <h3>Compliance Rules</h3>
          </div>

          <strong>{complianceRules.length} rules</strong>
        </div>

        {ruleLoadError && (
          <div className="compliance-warning">
            {ruleLoadError}
          </div>
        )}

        <div className="compliance-control-list">
          {complianceRules.length > 0 ? (
            complianceRules.map((rule) => (
              <article
                key={rule.id}
                className="compliance-control-card"
              >
                <div className="compliance-control-card-header">
                  <div>
                    <span className="compliance-control-id">
                      {rule.control_identifier}
                    </span>

                    <h3>{rule.rule_name}</h3>

                    <small>
                      {rule.framework_name}
                      {" • "}
                      {rule.control_title}
                    </small>
                  </div>

                  <div className="compliance-control-card-score">
                    <span>
                      {rule.enabled ? "Enabled" : "Disabled"}
                    </span>
                  </div>
                </div>

                <p>
                  {rule.description ||
                    "No rule description supplied."}
                </p>

                <button
                  type="button"
                  onClick={() =>
                    setSelectedComplianceRule(rule)
                  }
                >
                  View Rule
                </button>
              </article>
            ))
          ) : (
            <div className="compliance-empty">
              No compliance rules are currently available.
            </div>
          )}
        </div>
      </section>

      {selectedComplianceRule && (
        <section className="compliance-panel">
          <div className="compliance-panel-heading">
            <div>
              <span>RULE DETAIL</span>
              <h3>
                {selectedComplianceRule.rule_name}
              </h3>
            </div>

            <button
              type="button"
              onClick={() =>
                setSelectedComplianceRule(null)
              }
            >
              Close
            </button>
          </div>

          <div className="compliance-remediation-summary">
            <div>
              <span>Rule Key</span>
              <strong>
                {selectedComplianceRule.rule_key}
              </strong>
            </div>

            <div>
              <span>Evaluator</span>
              <strong>
                {selectedComplianceRule.evaluator_type}
              </strong>
            </div>

            <div>
              <span>Evidence Source</span>
              <strong>
                {selectedComplianceRule.source_record_type}
              </strong>
            </div>

            <div>
              <span>Implementation</span>
              <strong>
                {selectedComplianceRule.implementation_status}
              </strong>
            </div>

            <div>
              <span>Pass</span>
              <strong>
                {selectedComplianceRule.pass_score}
              </strong>
            </div>

            <div>
              <span>Partial</span>
              <strong>
                {selectedComplianceRule.partial_score}
              </strong>
            </div>

            <div>
              <span>Fail</span>
              <strong>
                {selectedComplianceRule.fail_score}
              </strong>
            </div>

            <div>
              <span>Priority</span>
              <strong>
                {selectedComplianceRule.priority}
              </strong>
            </div>
          </div>
        </section>
      )}

      {showCreateAssessment && (
        <section className="compliance-panel compliance-create-assessment">
          <div className="compliance-panel-heading">
            <div>
              <span>CONTINUOUS ASSESSMENT</span>
              <h3>Create Compliance Assessment</h3>
            </div>

            <button
              type="button"
              onClick={() =>
                setShowCreateAssessment(false)
              }
            >
              Close
            </button>
          </div>

          {createAssessmentError && (
            <div className="compliance-warning">
              {createAssessmentError}
            </div>
          )}

          <div className="compliance-remediation-summary">
            <label>
              Framework
              <select
                value={assessmentForm.framework_id}
                onChange={(event) =>
                  setAssessmentForm((current) => ({
                    ...current,
                    framework_id:
                      event.target.value,
                  }))
                }
              >
                <option value="">Select framework</option>

                {frameworks
                  .filter(
                    (framework) =>
                      framework.enabled
                  )
                  .map((framework) => (
                    <option
                      key={framework.id}
                      value={framework.id}
                    >
                      {framework.framework_name}
                      {" — "}
                      {framework.version}
                    </option>
                  ))}
              </select>
            </label>

            <label>
              Assessment Name
              <input
                type="text"
                value={
                  assessmentForm.assessment_name
                }
                onChange={(event) =>
                  setAssessmentForm((current) => ({
                    ...current,
                    assessment_name:
                      event.target.value,
                  }))
                }
              />
            </label>

            <label>
              Assessment Type
              <select
                value={
                  assessmentForm.assessment_type
                }
                onChange={(event) =>
                  setAssessmentForm((current) => ({
                    ...current,
                    assessment_type:
                      event.target.value,
                  }))
                }
              >
                <option value="continuous">
                  Continuous
                </option>
                <option value="point_in_time">
                  Point in Time
                </option>
              </select>
            </label>

            <label>
              Assessment Owner
              <input
                type="text"
                value={
                  assessmentForm.assessment_owner
                }
                onChange={(event) =>
                  setAssessmentForm((current) => ({
                    ...current,
                    assessment_owner:
                      event.target.value,
                  }))
                }
              />
            </label>
          </div>

          <label>
            Scope Description
            <textarea
              value={
                assessmentForm.scope_description
              }
              onChange={(event) =>
                setAssessmentForm((current) => ({
                  ...current,
                  scope_description:
                    event.target.value,
                }))
              }
            />
          </label>

          <button
            type="button"
            onClick={createAssessment}
            disabled={creatingAssessment}
          >
            {creatingAssessment
              ? "Creating Assessment..."
              : "Save Assessment"}
          </button>
        </section>
      )}

    </div>
  );
}

export default ComplianceCenterPage;
